import { useLocation, useNavigate, Location, NavigateFunction } from 'react-router-dom';

import { useAuth, AuthContextValue } from '../auth/AuthProvider';

type ApiState = {
	auth: AuthContextValue | null;
	location: Location;
	navigate: NavigateFunction;
};

type ApiResponseError = {
	message: string;
	type?: string;
	code?: number;
};

type ApiResponsePaging = {
	offset: number;
	limit: number;
	total: number;
};

type ApiResponse<T> = {
	status: 'ok' | 'error';
	data?: T;
	error?: ApiResponseError;
	paging?: ApiResponsePaging;
};

type FetchOptions = RequestInit & {
	contentType?: string;
};

class API {
	private state: ApiState;

	constructor(state: ApiState) {
		this.state = state;
	}

	fetchResponseHandler(res: Response): Promise<Response> {
		return new Promise((resolve, reject) => {
			if (res.status === 401) {
				res.json()
				.then((json: ApiResponse<unknown>) => {
					console.warn('401 json', json);

					if (json.error?.code === 401001 || json.error?.type === 'invalidauth') {
						this.state.auth?.clear();
						this.state.navigate("/auth/login", { state: { from: this.state.location }});
					} else if (json.error?.code === 401003 || json.error?.type === 'reauthrequired') {
						this.state.navigate("/auth/login", { state: { from: this.state.location, reauth: true }});
					} else {
						alert("Response was 401: " + json.error?.message);
					}

					reject("Response was 401: " + json.error?.message);
				})
				.catch((err: unknown) => {
					console.error(err);
					alert(err);
					reject(err);
				});
			} else if (res.status === 403) {
				res.json()
				.then((json: ApiResponse<unknown>) => {
					console.warn('403 json', json);
					alert(json.error?.message ?? 'Response was 403 Forbidden');
					reject(json.error?.message ?? 'Response was 403 Forbidden');
				})
				.catch((err: unknown) => {
					console.error(err);
					alert(err);
					reject(err);
				});
			} else if (res.status === 400) {
				res.json()
				.then((json: ApiResponse<unknown>) => {
					console.warn(`${res.status} json`, json);
					alert(json.error?.message);
					reject(json.error?.message);
				})
				.catch((err: unknown) => {
					console.error(err);
					alert(err);
					reject(err);
				});
			} else if (res.status === 404 || res.status === 500 || res.status === 504) {
				res.json()
				.then((json: ApiResponse<unknown>) => {
					console.warn(`${res.status} json`, json);
					alert(json.error?.message);
					reject(json.error?.message);
				})
				.catch((err: unknown) => {
					console.error(err);
					reject(err);
				});
			} else {
				resolve(res);
			}
		});
	}

	fetchErrorHandler(err: unknown): void {
		console.error(err);
	}

	fetch<T = unknown>(api: string, options?: FetchOptions): Promise<T> {
		return new Promise((resolve, reject) => {
			fetch(api, {
				...options,
				headers: {
					...(options && options.headers),
				},
			})
			.then(res => this.fetchResponseHandler(res))
			.then(res => res.json() as Promise<ApiResponse<T>>)
			.then(json => resolve(json.data as T))
			.catch(err => {
				this.fetchErrorHandler(err);
				reject(err);
			});
		});
	}

	get<T = unknown>(api: string, options?: FetchOptions): Promise<T> {
		return this.fetch<T>(api, { method: 'GET', ...options });
	}

	post<T = unknown>(api: string, data: unknown, options?: FetchOptions): Promise<T> {
		return this.fetch<T>(api, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data),
			...options
		});
	}

	put<T = unknown>(api: string, data: unknown, options?: FetchOptions): Promise<T> {
		return this.fetch<T>(api, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data),
			...options
		});
	}

	delete<T = unknown>(api: string, options?: FetchOptions): Promise<T> {
		return this.fetch<T>(api, {
			method: 'DELETE',
			...options
		});
	}

	upload<T = unknown>(api: string, file: Blob | ArrayBuffer, options?: FetchOptions): Promise<T> {
		return this.fetch<T>(api, {
			method: 'POST',
			contentType: 'application/octet-stream',
			body: file,
			...options
		});
	}
}

const useAPI = () => {
	return new API({
		auth: useAuth(),
		location: useLocation(),
		navigate: useNavigate()
	});
};

export { useAPI, API };
export type { ApiResponse, ApiResponseError, ApiResponsePaging };
